import {
  Alert,
  AlertTitle,
  Button,
  Form,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@design-system';
import {
  QuickPlanPartnerDialog,
  QuickPlanPersonFields,
  quickPlanPersonSchema,
  useQuickPlanForm,
  type QuickPlanFormValues,
} from '@/features/client-quick-plan';

import { AlertCircle, ArrowRight, UserPen, UserPlus, X } from 'lucide-react';

import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';

interface QuickPlanFormProps {
  onSubmit: (data: QuickPlanFormValues) => void;
  isSubmitting?: boolean;
  variant?: 'dialog' | 'card';
  onPartnerDialogChange?: (open: boolean) => void;
}

export function QuickPlanForm({
  onSubmit,
  isSubmitting = false,
  variant = 'card',
  onPartnerDialogChange,
}: QuickPlanFormProps) {
  const { flags } = useFeatureFlags();
  const [showPartnerPanel, setShowPartnerPanel] = useState(false);
  const {
    form,
    hasPartner,
    canAddPartner,
    partnerLabel,
    handleTogglePartner,
    handleMaritalStatusChange,
    handlePartnerMaritalStatusChange,
    onSubmit: handleSubmit,
    isValid,
  } = useQuickPlanForm({ onSubmit });

  const showPartnerOption = flags.coupleQuickPlan && canAddPartner;

  const partnerValues = form.watch('partner');
  const isPartnerValid = partnerValues
    ? quickPlanPersonSchema.safeParse(partnerValues).success
    : false;

  useEffect(() => {
    onPartnerDialogChange?.(showPartnerPanel);
  }, [showPartnerPanel, onPartnerDialogChange]);

  function handleAddPartner() {
    handleTogglePartner(true);
    setShowPartnerPanel(true);
  }

  function handleRemovePartner() {
    handleTogglePartner(false);
    setShowPartnerPanel(false);
  }

  function handlePartnerDone() {
    setShowPartnerPanel(false);
  }

  function handlePartnerCancel() {
    if (isPartnerValid) {
      setShowPartnerPanel(false);
    } else {
      handleTogglePartner(false);
      setShowPartnerPanel(false);
    }
  }

  function handlePartnerDialogOpenChange(open: boolean) {
    if (!open) {
      handlePartnerCancel();
    } else {
      setShowPartnerPanel(true);
    }
  }

  const capitalizedLabel = partnerLabel.charAt(0).toUpperCase() + partnerLabel.slice(1);

  const showCardPartnerView = variant === 'card' && showPartnerPanel;

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        {!showCardPartnerView && (
          <>
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Alert
                      variant="info"
                      className="gap-x-2! cursor-help p-3"
                    >
                      <AlertCircle />
                      <AlertTitle>Quick plans use limited data</AlertTitle>
                    </Alert>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  For a full plan with personalized projections, have your client complete the
                  intake form.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <fieldset disabled={isSubmitting}>
              <div className="space-y-6">
                <QuickPlanPersonFields
                  control={form.control}
                  onMaritalStatusChange={handleMaritalStatusChange}
                />
              </div>
            </fieldset>

            {showPartnerOption && hasPartner && partnerValues && (
              <div className="border-border bg-muted flex items-start justify-between rounded-lg border p-3">
                <div className="flex min-w-0 flex-1 flex-col gap-0.5 px-0.5">
                  <p className="text-sm font-medium">
                    {capitalizedLabel}: {partnerValues.firstName} {partnerValues.lastName}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {partnerValues.birthDate ? format(partnerValues.birthDate, 'MMM d, yyyy') : ''}{' '}
                    · {partnerValues.sex === 'male' ? 'Male' : 'Female'} · {partnerValues.zipCode}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Tooltip>
                    <TooltipTrigger type="button">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7 rounded-sm"
                        onClick={() => setShowPartnerPanel(true)}
                      >
                        <UserPen />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="px-2">Edit</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger type="button">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive size-7 rounded-sm"
                        onClick={handleRemovePartner}
                      >
                        <X />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="px-2">Remove</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {showPartnerOption && !hasPartner && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleAddPartner}
                >
                  <UserPlus />
                  Add {partnerLabel}
                </Button>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={!isValid || isSubmitting}
              >
                {isSubmitting ? 'Generating...' : 'Generate quick plan'}
                {!isSubmitting && <ArrowRight />}
              </Button>
            </div>
          </>
        )}

        {showCardPartnerView && (
          <>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-gray-950">{capitalizedLabel} Details</h3>
              <p className="text-foreground text-sm">
                Enter your client&apos;s {partnerLabel} information
              </p>
            </div>

            <fieldset className="space-y-6">
              <QuickPlanPersonFields
                control={form.control}
                fieldPrefix="partner."
                onMaritalStatusChange={handlePartnerMaritalStatusChange}
              />
            </fieldset>

            <div className="flex w-full gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handlePartnerCancel}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="flex-1"
                disabled={!isPartnerValid}
                onClick={handlePartnerDone}
              >
                Done
              </Button>
            </div>
          </>
        )}

        {variant === 'dialog' && (
          <QuickPlanPartnerDialog
            open={showPartnerPanel}
            onOpenChange={handlePartnerDialogOpenChange}
            form={form}
            partnerLabel={partnerLabel}
            onMaritalStatusChange={handlePartnerMaritalStatusChange}
            onDone={handlePartnerDone}
            onCancel={handlePartnerCancel}
            isPartnerValid={isPartnerValid}
          />
        )}
      </form>
    </Form>
  );
}
